import UnControlledReactSelect from 'components/React-Select-All/UnControlledReactSelect';
import useGlobalState from 'custom_hooks/useGlobalState';
import { useState } from 'react';
import moment from 'moment-timezone';
import CommonModal from 'components/Modals/CommonModal';
import DayPicker, { DateUtils, Modifier, RangeModifier } from 'react-day-picker';
import { PrimaryButton, SecondaryButton } from 'css/components/Modules/Buttons';
import styled from 'styled-components';
import momentDateFormat from 'helpers/momentDateFormat';
import { getStartAndEndOfWeek, ISOWeekDay } from 'utils/getStartAndEndOfWeek';
import { isHoliday, isWeekend } from 'utils/dateFns';
import { theme } from 'css/utils';

const SelectWrapper = styled.div`
  position: relative;

  .custom-date-value-container {
    background-color: ${({ disabled }) => (disabled ? theme.disabledField : 'white')};
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    z-index: 1;
  }
`;

/**
 * Returns the previous work day based on the showWeekends and showHolidays flags.
 * If showWeekends is false, it will return the previous day that is not a weekend.
 * If showHolidays is true, it will return the previous day that is not a holiday.
 * @param showWeekends - A boolean flag indicating whether to show weekends.
 * @param showHolidays - A boolean flag indicating whether to show holidays.
 */
const getPreviousWorkDay = (showWeekends: boolean, showHolidays: boolean) => {
  let date = moment().subtract(1, 'day');

  while ((!showWeekends && isWeekend(date)) || (!showHolidays && isHoliday(date))) {
    date = date.subtract(1, 'day');
  }

  return {
    from: date.clone().startOf('day').toDate(),
    to: date.clone().endOf('day').toDate()
  };
};

export type DateRangeType =
  | 'Previous Work Day'
  | 'This Week'
  | 'Last Week'
  | 'Last Month'
  | 'Custom'
  | 'Today';

export interface WidgetDateOption {
  label: DateRangeType;
  value: {
    from: Date;
    to: Date;
  };
  isCustom?: boolean;
  selected?: boolean;
}

interface CustomDateRange {
  from: Date | null | undefined;
  to: Date | null | undefined;
}

interface DateOptionArgs {
  showWeekends?: boolean;
  showToday?: boolean;
  startOfWeek?: ISOWeekDay;
  showHolidays?: boolean;
  defaultLabel?: string;
}

export const getWidgetDateOptions = ({
  showWeekends = true,
  showToday,
  startOfWeek,
  showHolidays = true,
  defaultLabel = ''
}: DateOptionArgs = {}): WidgetDateOption[] => {
  const options: WidgetDateOption[] = [
    ...(showToday
      ? [
          {
            label: 'Today' as const,
            value: {
              from: moment().startOf('day').toDate(),
              to: moment().endOf('day').toDate()
            },
            selected: defaultLabel === 'Today'
          }
        ]
      : []),
    {
      label: 'Previous Work Day',
      value: getPreviousWorkDay(showWeekends, showHolidays),
      selected: defaultLabel === 'Previous Work Day'
    },
    {
      label: 'This Week',
      value: {
        from: getStartAndEndOfWeek(startOfWeek).from.startOf('day').toDate(),
        to: moment
          .min(getStartAndEndOfWeek(startOfWeek).to.endOf('day'), moment().endOf('day'))
          .toDate()
      },
      selected: defaultLabel === 'This Week'
    },
    {
      label: 'Last Week',
      value: {
        from: getStartAndEndOfWeek(startOfWeek).from.subtract(1, 'week').startOf('day').toDate(),
        to: getStartAndEndOfWeek(startOfWeek).to.subtract(1, 'week').endOf('day').toDate()
      },
      selected: defaultLabel === 'Last Week'
    },
    {
      label: 'Last Month',
      value: {
        from: moment().subtract(1, 'month').startOf('month').startOf('day').toDate(),
        to: moment().subtract(1, 'month').endOf('month').endOf('day').toDate()
      },
      selected: defaultLabel === 'Last Month'
    },
    {
      label: 'Custom',
      value: {
        from: new Date(),
        to: new Date()
      },
      isCustom: true,
      selected: defaultLabel === 'Custom'
    }
  ];

  return options;
};

interface DateSelectProps {
  onDateChange: (value: WidgetDateOption) => void;
  defaultValue: WidgetDateOption;
  showToday?: boolean;
  disabled?: boolean;
}

const DateSelect = ({
  onDateChange,
  defaultValue,
  showToday = false,
  disabled = false
}: DateSelectProps) => {
  const { Weekends, StartOfWeek } = useGlobalState();
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>();

  const DATE_OPTIONS = getWidgetDateOptions({
    showWeekends: Weekends,
    showToday,
    startOfWeek: StartOfWeek,
    defaultLabel: defaultValue.label
  });

  const selectDateOption = (option: WidgetDateOption) => {
    if (option.isCustom) {
      setShowCustomDate(true);
    } else {
      onDateChange(option);
    }
  };

  const toggleCustomDatePicker = () => {
    setShowCustomDate(prev => !prev);
  };

  const handleDayClick = (day: Date) => {
    if (moment(day).isSameOrAfter(moment().startOf('day'))) {
      return;
    }

    let range: CustomDateRange;

    if (customDateRange?.from && customDateRange?.to) {
      range = { from: day, to: undefined };
    } else {
      range = DateUtils.addDayToRange(day, customDateRange as RangeModifier);
    }

    const maxAllowedDays = 31;

    if (range.from && range.to) {
      const daysBetween = moment(range.to).diff(moment(range.from), 'days');
      if (daysBetween >= maxAllowedDays) {
        return;
      }
    }

    setCustomDateRange(range);
  };

  const handleCustomDateSubmit = () => {
    onDateChange({
      label: 'Custom',
      isCustom: true,
      value: {
        from: moment(customDateRange?.from).startOf('day').toDate(),
        to: moment(customDateRange?.to).endOf('day').toDate()
      }
    });

    toggleCustomDatePicker();
  };

  return (
    <>
      {showCustomDate && (
        <CommonModal isOpen={showCustomDate} toggle={toggleCustomDatePicker} noHeader>
          <DayPicker
            className='Selectable'
            month={customDateRange?.from || new Date()}
            selectedDays={[
              customDateRange?.from as unknown as Date,
              {
                from: customDateRange?.from as unknown as Date,
                to: customDateRange?.to as unknown as Date
              }
            ]}
            modifiers={{
              start: customDateRange?.from as unknown as Modifier,
              end: customDateRange?.to as unknown as Modifier
            }}
            disabledDays={{ after: moment().subtract(1, 'day').startOf('day').toDate() }}
            onDayClick={handleDayClick}
          />

          <div className='d-flex justify-content-between align-items-center p-3'>
            <PrimaryButton
              modifiers={['small', 'minWidth120']}
              onClick={handleCustomDateSubmit}
              disabled={!customDateRange?.from || !customDateRange?.to}
            >
              Submit
            </PrimaryButton>

            <SecondaryButton modifiers={['small', 'minWidth120']} onClick={toggleCustomDatePicker}>
              Cancel
            </SecondaryButton>
          </div>
        </CommonModal>
      )}

      <SelectWrapper disabled={disabled}>
        {defaultValue.isCustom ? (
          <div className='custom-date-value-container text-md text-muted'>
            {momentDateFormat(defaultValue.value.from)} - {momentDateFormat(defaultValue.value.to)}
          </div>
        ) : null}

        <UnControlledReactSelect
          key={String(showCustomDate)}
          defaultValue={undefined}
          options={DATE_OPTIONS}
          selectHandler={(_: any, option: WidgetDateOption) => {
            selectDateOption(option);
          }}
          isSearchable={false}
          clearable={false}
          isMulti={false}
          closeMenuOnSelect={true}
          hideSelectedOptions={false}
          noOptionsMessage={() => 'No options found'}
          menuPlacement={'bottom'}
          valueProp={defaultValue}
          placeholder={'Select Date'}
          disable={disabled}
        />
      </SelectWrapper>
    </>
  );
};

export default DateSelect;
